// Runs that could not reach the board (offline, transient failure) wait in the save and are retried on boot,
// when the browser comes back online, and after each successful submission. Pure helpers: they return new
// objects and never touch the network themselves.
import type { RunResult, SaveData, SubmitOutcome } from '../types';
import { MAX_PENDING } from '../save';

/** Appends a result to the queue, oldest first; past MAX_PENDING the oldest are dropped. */
export function enqueuePending(save: SaveData, result: RunResult): SaveData {
  return { ...save, pending: [...save.pending, result].slice(-MAX_PENDING) };
}

export interface DrainReport {
  /** The save with the processed runs removed from `pending`. */
  save: SaveData;
  submitted: number;
  /** Runs the board refused outright ('rejected'); they are dropped, retrying would not help. */
  dropped: number;
  /** The outcome that stopped the drain ('offline', 'unconfigured', 'error', …), or null when the queue ran dry. */
  stopped: SubmitOutcome | null;
}

/**
 * Submits queued runs in order. A 'rejected' run is dropped; any other failure stops the drain and keeps
 * that run and everything behind it for next time. A throwing `submit` counts as a transient error.
 */
export async function drainPending(save: SaveData, submit: (r: RunResult) => Promise<SubmitOutcome>): Promise<DrainReport> {
  const queue = [...save.pending];
  let submitted = 0;
  let dropped = 0;
  let stopped: SubmitOutcome | null = null;
  while (queue.length) {
    let outcome: SubmitOutcome;
    try {
      outcome = await submit(queue[0]);
    } catch {
      outcome = { ok: false, reason: 'error' };
    }
    if (outcome.ok) {
      submitted++;
      queue.shift();
    } else if (outcome.reason === 'rejected') {
      dropped++;
      queue.shift();
    } else {
      stopped = outcome;
      break;
    }
  }
  return { save: { ...save, pending: queue }, submitted, dropped, stopped };
}
